import { type ApprovalRequestId } from "@taskin-code/contracts";
import { memo, useCallback, useEffect, useRef } from "react";
import { type PendingUserInput } from "../../session-logic";
import {
  derivePendingUserInputProgress,
  type PendingUserInputDraftAnswer,
} from "../../pendingUserInput";
import { CheckIcon, ChevronDownIcon } from "~/lib/icons";
import { cn } from "~/lib/utils";

interface PendingUserInputPanelProps {
  pendingUserInputs: PendingUserInput[];
  respondingRequestIds: ApprovalRequestId[];
  answers: Record<string, PendingUserInputDraftAnswer>;
  questionIndex: number;
  isCollapsed: boolean;
  onToggleOption: (questionId: string, optionLabel: string) => void;
  onAdvance: () => void;
  onToggleCollapsed: () => void;
}

export const ComposerPendingUserInputPanel = memo(function ComposerPendingUserInputPanel({
  pendingUserInputs,
  respondingRequestIds,
  answers,
  questionIndex,
  isCollapsed,
  onToggleOption,
  onAdvance,
  onToggleCollapsed,
}: PendingUserInputPanelProps) {
  if (pendingUserInputs.length === 0) return null;
  const activePrompt = pendingUserInputs[0];
  if (!activePrompt) return null;

  return (
    <ComposerPendingUserInputCard
      key={activePrompt.requestId}
      prompt={activePrompt}
      isResponding={respondingRequestIds.includes(activePrompt.requestId)}
      answers={answers}
      questionIndex={questionIndex}
      isCollapsed={isCollapsed}
      onToggleOption={onToggleOption}
      onAdvance={onAdvance}
      onToggleCollapsed={onToggleCollapsed}
    />
  );
});

const ComposerPendingUserInputCard = memo(function ComposerPendingUserInputCard({
  prompt,
  isResponding,
  answers,
  questionIndex,
  isCollapsed,
  onToggleOption,
  onAdvance,
  onToggleCollapsed,
}: {
  prompt: PendingUserInput;
  isResponding: boolean;
  answers: Record<string, PendingUserInputDraftAnswer>;
  questionIndex: number;
  isCollapsed: boolean;
  onToggleOption: (questionId: string, optionLabel: string) => void;
  onAdvance: () => void;
  onToggleCollapsed: () => void;
}) {
  const progress = derivePendingUserInputProgress(prompt.questions, answers, questionIndex);
  const activeQuestion = progress.activeQuestion;
  const autoAdvanceTimerRef = useRef<number | null>(null);

  // Clear auto-advance timer on unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceTimerRef.current !== null) {
        window.clearTimeout(autoAdvanceTimerRef.current);
      }
    };
  }, []);

  const handleOptionSelection = useCallback(
    (questionId: string, optionLabel: string) => {
      onToggleOption(questionId, optionLabel);
      if (activeQuestion?.multiSelect) {
        return;
      }
      if (autoAdvanceTimerRef.current !== null) {
        window.clearTimeout(autoAdvanceTimerRef.current);
      }
      autoAdvanceTimerRef.current = window.setTimeout(() => {
        autoAdvanceTimerRef.current = null;
        onAdvance();
      }, 200);
    },
    [activeQuestion?.multiSelect, onAdvance, onToggleOption],
  );

  // Keyboard shortcut: digits toggle options for multi-select prompts and preserve
  // the current auto-advance behavior for single-select questions.
  useEffect(() => {
    // Collapsed, the options are off screen; a stray digit would answer a
    // question the user cannot currently read.
    if (!activeQuestion || isResponding || isCollapsed) return;
    const handler = (event: globalThis.KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target;
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
        return;
      }
      // Let digit input pass through whenever focus is inside an editable region,
      // including nested contenteditable descendants inside the composer.
      if (
        target instanceof HTMLElement &&
        target.closest('[contenteditable]:not([contenteditable="false"])')
      ) {
        return;
      }
      const digit = Number.parseInt(event.key, 10);
      if (Number.isNaN(digit) || digit < 1 || digit > 9) return;
      const optionIndex = digit - 1;
      if (optionIndex >= activeQuestion.options.length) return;
      const option = activeQuestion.options[optionIndex];
      if (!option) return;
      event.preventDefault();
      handleOptionSelection(activeQuestion.id, option.label);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [activeQuestion, handleOptionSelection, isCollapsed, isResponding]);

  if (!activeQuestion) {
    return null;
  }

  return (
    <div className="px-4 py-3 sm:px-5">
      {/*
       * The whole header is the collapse control, not a small chevron target:
       * this panel can take half a phone screen away from the conversation that
       * prompted the question, and reaching the message you need to re-read has
       * to be one comfortable tap.
       */}
      <button
        type="button"
        onClick={onToggleCollapsed}
        aria-expanded={!isCollapsed}
        className="group -mx-1 flex w-full items-center gap-3 rounded-md px-1 py-0.5 text-left transition-colors hover:bg-muted/30"
      >
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {prompt.questions.length > 1 ? (
            <span className="flex h-5 shrink-0 items-center rounded-md bg-muted/60 px-1.5 text-[10px] font-medium tabular-nums text-muted-foreground/60">
              {questionIndex + 1}/{prompt.questions.length}
            </span>
          ) : null}
          <span className="shrink-0 text-[11px] font-semibold tracking-widest text-muted-foreground/50 uppercase">
            {activeQuestion.header}
          </span>
          {/* Collapsed, the header is all that is left, so it has to carry
              enough of the question to be worth coming back to. */}
          {isCollapsed ? (
            <span className="min-w-0 truncate text-xs text-muted-foreground/60">
              {activeQuestion.question}
            </span>
          ) : null}
        </div>
        <ChevronDownIcon
          aria-hidden="true"
          className={cn(
            "size-3.5 shrink-0 text-muted-foreground/50 transition-transform duration-150 group-hover:text-muted-foreground/80",
            isCollapsed ? "-rotate-90" : "",
          )}
        />
        <span className="sr-only">
          {isCollapsed ? "Expand the pending question" : "Collapse the pending question"}
        </span>
      </button>
      {isCollapsed ? null : (
        <>
          <p className="mt-1.5 text-sm text-foreground/90">{activeQuestion.question}</p>
          {activeQuestion.multiSelect ? (
            <p className="mt-1 text-xs text-muted-foreground/65">Select one or more options.</p>
          ) : null}
          <div className="mt-3 space-y-1">
            {activeQuestion.options.map((option, index) => {
              const isSelected = progress.selectedOptionLabels.includes(option.label);
              const shortcutKey = index < 9 ? index + 1 : null;
              return (
                <button
                  key={`${activeQuestion.id}:${option.label}`}
                  type="button"
                  disabled={isResponding}
                  onClick={() => handleOptionSelection(activeQuestion.id, option.label)}
                  className={cn(
                    "group flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left transition-all duration-150",
                    isSelected
                      ? "border-blue-500/40 bg-blue-500/8 text-foreground"
                      : "border-transparent bg-muted/20 text-foreground/80 hover:bg-muted/40 hover:border-border/40",
                    isResponding && "opacity-50 cursor-not-allowed",
                  )}
                >
                  {shortcutKey !== null ? (
                    <kbd
                      className={cn(
                        "flex size-5 shrink-0 items-center justify-center rounded text-[11px] font-medium tabular-nums transition-colors duration-150",
                        isSelected
                          ? "bg-blue-500/20 text-blue-400"
                          : "bg-muted/40 text-muted-foreground/50 group-hover:bg-muted/60 group-hover:text-muted-foreground/70",
                      )}
                    >
                      {shortcutKey}
                    </kbd>
                  ) : null}
                  <div className="min-w-0 flex-1">
                    <span className="text-sm font-medium">{option.label}</span>
                    {option.description && option.description !== option.label ? (
                      // Trailing on a wide row, its own line on a phone: inline, a
                      // two-sentence description runs straight into the label and
                      // the choice stops being scannable.
                      <span className="block text-xs text-muted-foreground/50 sm:ml-2 sm:inline">
                        {option.description}
                      </span>
                    ) : null}
                  </div>
                  {isSelected ? <CheckIcon className="size-3.5 shrink-0 text-blue-400" /> : null}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
});
